import type { RouteLocationRaw } from 'vue-router'
import { isOpenScreenId, routeForScreen, screenLabel } from '@/router/screens'
import type { AgentCopyParams, AgentOutcome } from '@/types/chat'

/** One outcome row to render: i18n key, params, and the route a linking outcome opens (`null` when it cannot resolve). */
export interface OutcomeRow {
  key: string
  params?: AgentCopyParams
  route?: RouteLocationRaw | null
}

/** Projects `AgentOutcome`s onto rows `AppOutcomes` can render, resolving `artifactLinked`'s route. */
export function toOutcomeRows(outcomes: readonly AgentOutcome[]): OutcomeRow[] {
  return outcomes.map((outcome) => {
    const key = `chat.activity.outcome.${outcome.key}`

    if (outcome.key === 'artifactLinked') {
      const screen = outcome.params?.screen
      const itemId = outcome.params?.itemId
      if (typeof screen !== 'string' || !isOpenScreenId(screen))
        return { key, params: outcome.params, route: null }
      return {
        key,
        params: { ...outcome.params, name: screenLabel(screen) },
        route: routeForScreen(screen, typeof itemId === 'string' ? itemId : undefined),
      }
    }

    if (outcome.key === 'designRunStarted' || outcome.key === 'designAttachedToPost') {
      const designId = outcome.params?.designId
      const route = typeof designId === 'string' ? routeForScreen('design', designId) : null
      return { key, params: outcome.params, route }
    }

    if (outcome.key === 'topicAdded') {
      return { key, params: outcome.params, route: routeForScreen('pages') }
    }

    return { key, params: outcome.params }
  })
}

export function dedupeOutcomes(outcomes: readonly AgentOutcome[]): AgentOutcome[] {
  const seen = new Map<string, AgentOutcome>()
  for (const outcome of outcomes) {
    seen.set(`${outcome.key}:${JSON.stringify(outcome.params ?? {})}`, outcome)
  }
  return [...seen.values()]
}

/** One design edit Raldo started: the design and the run that edits it. */
export interface StartedDesignEdit {
  designId: string
  runId: string
}

/** The design edits a set of outcomes says Raldo started, each once, in call order; an outcome without a run id is left out. */
export function startedDesignEdits(outcomes: readonly AgentOutcome[]): StartedDesignEdit[] {
  const edits = new Map<string, StartedDesignEdit>()
  for (const outcome of outcomes) {
    const designId = outcome.params?.designId
    const runId = outcome.params?.runId
    if (
      outcome.key === 'designEditStarted' &&
      typeof designId === 'string' &&
      typeof runId === 'string'
    )
      edits.set(runId, { designId, runId })
  }
  return [...edits.values()]
}

/** The outcomes that read as a row under the answer; a started design edit is a card of its own. */
export function withoutDesignEdits(outcomes: readonly AgentOutcome[]): AgentOutcome[] {
  return outcomes.filter((outcome) => outcome.key !== 'designEditStarted')
}
